import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../services/axios';
import '../styles/HistorialCambiosPage.css';

function HistorialCambiosPage() {
  const [historial, setHistorial] = useState([]);
  const [filteredHistorial, setFilteredHistorial] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState('');
  const location = useLocation(); 
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; 
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1025);

  const fetchHistorial = async () => {
    try {
      setLoading(true);
      const response = await api.get('/historial');
      setHistorial(response.data);
      setError('');
    } catch (err) {
      setError('Error al obtener el historial de cambios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistorial();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      const filtered = historial.filter(item => item.tabla_afectada === selectedTable);
      setFilteredHistorial(filtered);
    } else {
      setFilteredHistorial(historial);
    }
    setCurrentPage(1);
  }, [historial, selectedTable]);

  const totalItems = filteredHistorial.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentHistorial = filteredHistorial.slice(startIndex, endIndex);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1025;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    if (window.innerWidth >= 1025) {
      setSidebarOpen(true);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const getActionIcon = (accion) => {
    switch (accion.toLowerCase()) {
      case 'crear':
      case 'insert':
        return 'fas fa-plus-circle';
      case 'actualizar':
      case 'update':
        return 'fas fa-edit';
      case 'eliminar':
      case 'delete':
        return 'fas fa-trash-alt';
      default:
        return 'fas fa-file-alt';
    }
  };

  const getActionColor = (accion) => {
    switch (accion.toLowerCase()) {
      case 'crear':
      case 'insert':
        return '#00ff88';
      case 'actualizar':
      case 'update':
        return '#ffaa00';
      case 'eliminar':
      case 'delete':
        return '#ff4444';
      default:
        return '#00aaff';
    }
  };

  const getActionBadgeColor = (accion) => {
    switch (accion.toLowerCase()) {
      case 'crear':
      case 'insert':
        return 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)';
      case 'actualizar':
      case 'update':
        return 'linear-gradient(135deg, #ffaa00 0%, #ff8800 100%)';
      case 'eliminar':
      case 'delete':
        return 'linear-gradient(135deg, #ff4444 0%, #cc3333 100%)';
      default:
        return 'linear-gradient(135deg, #00aaff 0%, #0088cc 100%)';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatData = (dataString) => {
    if (!dataString) return 'Sin datos';
    try {
      const data = JSON.parse(dataString);
      return Object.entries(data)
        .map(([key, value]) => {
          // Formatear nombres de campos más legibles
          const fieldNames = {
            'nombre': 'Nombre',
            'apellido_paterno': 'Apellido Paterno',
            'apellido_materno': 'Apellido Materno',
            'correo': 'Correo',
            'rol': 'Rol',
            'nombre_curso': 'Nombre del Curso',
            'codigo_curso': 'Código del Curso',
            'numero_telefonico': 'Teléfono',
            'fecha_contacto': 'Fecha de Contacto',
            'tipo_red_social': 'Red Social',
            'curso_interes': 'Curso de Interés',
            'dia': 'Día',
            'horario_preferencia': 'Horario',
            'tomo_demostracion': 'Tomó Demostración',
            'estado_proceso': 'Estado del Proceso',
            'pais_origen': 'País de Origen',
            'estado': 'Estado',
            'fecha_inscripcion': 'Fecha de Inscripción',
            'curso_actual': 'Curso Actual',
            'estado_actividad': 'Estado de Actividad',
            'numero_matricula': 'Número de Matrícula',
            'fecha_baja': 'Fecha de Baja',
            'motivo_baja': 'Motivo de Baja',
            'observaciones': 'Observaciones',
            'notifico_baja': 'Notificó Baja'
          };
          
          const fieldName = fieldNames[key] || key;
          const fieldValue = value === null || value === undefined ? 'N/A' : value;
          return `${fieldName}: ${fieldValue}`;
        })
        .join('\n');
    } catch {
      return dataString;
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="dashboard-container">
      {(!isMobile || (isMobile && sidebarOpen)) && (
        <Sidebar 
          onLogoClick={() => { if (isMobile) setSidebarOpen(false); }}
          onLinkClick={handleCloseSidebar}
        />
      )}
      <div className="main-content" onClick={handleCloseSidebar}>
        <div className="dashboard-title-card">
          {isMobile && (
            <button 
              className="menu-toggle"
              onClick={toggleSidebar}
              aria-label="Abrir menú"
            >
              <i className="fas fa-bars"></i>
            </button>
          )}
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">Historial de Cambios</h1>
            <span className="dashboard-title-sub">Registro de modificaciones del sistema</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

        <div className="filter-container">
          <select 
            value={selectedTable} 
            onChange={(e) => setSelectedTable(e.target.value)}
            className="filter-select"
          >
            <option value="">Todas las tablas</option>
            <option value="usuarios">Usuarios</option>
            <option value="cursos">Cursos</option>
            <option value="prospectos">Prospectos</option>
            <option value="alumnos">Alumnos</option>
                            {/* <option value="bajas">Bajas</option> */}
            <option value="redes_sociales">Redes Sociales</option>
          </select>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Cargando historial...</p>
          </div>
        ) : (
          <>
            <div className="futuristic-table-wrapper">
              <div className="table-header-futuristic">
                <div className="header-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total de Cambios</span>
                    <span className="stat-value">{historial.length}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Filtrados</span>
                    <span className="stat-value">{filteredHistorial.length}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Mostrando</span>
                    <span className="stat-value">{currentHistorial.length}</span>
                  </div>
                </div>
              </div>
              
              <div className="futuristic-table">
                {currentHistorial.length > 0 ? (
                  currentHistorial.map((cambio) => {
                    const {
                      id_historial,
                      tabla_afectada,
                      usuario,
                      accion,
                      datos_anteriores,
                      datos_nuevos,
                      fecha_cambio
                    } = cambio;

                    return (
                      <div key={id_historial} className="table-row-futuristic">
                        <div className="row-header">
                          <div className="header-left">
                            <div className="prospecto-id">
                              <span className="id-label">ID</span>
                              <span className="id-value">#{id_historial}</span>
                            </div>
                            <div className="action-badge" style={{ background: getActionBadgeColor(accion) }}>
                              <i className={getActionIcon(accion)}></i>
                              <span>{accion}</span>
                            </div>
                          </div>
                          <div className="header-right">
                            <div className="timestamp">
                              <i className="fas fa-clock"></i>
                              <span>{formatDate(fecha_cambio)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="row-content">
                          <div className="content-grid">
                            <div className="info-section">
                              <div className="section-title">
                                <i className="fas fa-info-circle"></i>
                                <span>Información General</span>
                              </div>
                              <div className="info-cards">
                                <div className="info-card">
                                  <div className="info-card-header">
                                    <i className="fas fa-user"></i>
                                    <span>Usuario</span>
                                  </div>
                                  <div className="info-card-value">{usuario}</div>
                                </div>
                                <div className="info-card">
                                  <div className="info-card-header">
                                    <i className="fas fa-database"></i>
                                    <span>Tabla</span>
                                  </div>
                                  <div className="info-card-value">{tabla_afectada}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="data-section">
                              <div className="section-title">
                                <i className="fas fa-exchange-alt"></i>
                                <span>Cambios Realizados</span>
                              </div>
                              <div className="data-cards">
                                <div className="data-card previous">
                                  <div className="data-card-header">
                                    <i className="fas fa-history"></i>
                                    <span>Datos Anteriores</span>
                                  </div>
                                  <div className="data-card-content">
                                    {formatData(datos_anteriores) ? (
                                      <div className="data-text">
                                        {truncateText(formatData(datos_anteriores), 150)}
                                      </div>
                                    ) : (
                                      <div className="no-data">Sin datos previos</div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="data-card current">
                                  <div className="data-card-header">
                                    <i className="fas fa-check-circle"></i>
                                    <span>Datos Nuevos</span>
                                  </div>
                                  <div className="data-card-content">
                                    {formatData(datos_nuevos) ? (
                                      <div className="data-text">
                                        {truncateText(formatData(datos_nuevos), 150)}
                                      </div>
                                    ) : (
                                      <div className="no-data">Sin datos nuevos</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="fas fa-history"></i>
                    </div>
                    <h3>No hay cambios para mostrar</h3>
                    <p>El historial de cambios aparecerá aquí cuando se realicen modificaciones en el sistema.</p>
                  </div>
                )}
              </div>
            </div>

            {totalPages > 1 && (
              <div className="pagination-container">
                <button 
                  className="pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i> Anterior
                </button>
                
                <span className="page-info">
                  Página {currentPage} de {totalPages}
                </span>
                
                <button 
                  className="pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Siguiente <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {isMobile && sidebarOpen && (
        <div className="overlay" onClick={handleCloseSidebar} />
      )}
    </div>
  );
}

export default HistorialCambiosPage;
